import { useEffect, useState } from 'react';
import type { DisplayPublic } from '../api';
import { ChannelChrome } from './ChannelChrome';

interface StandingsRow {
  teamId: string;
  teamName: string;
  abbreviation: string | null;
  colorPrimary: string | null;
  wins: number;
  losses: number;
  ties: number;
  gamesPlayed: number;
  pointsFor: number;
  pointsAgainst: number;
  pointDifferential: number;
  winPct: number;
}

const REFRESH_MS = 60_000;

export function StandingsChannel({
  display,
  connected,
  onUnpair,
}: {
  display: DisplayPublic;
  connected: boolean;
  onUnpair: () => void;
}) {
  const [rows, setRows] = useState<StandingsRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const token =
      typeof localStorage !== 'undefined'
        ? JSON.parse(localStorage.getItem('scorelync-display') ?? '{}')
            ?.state?.displayToken
        : null;
    if (!token) return;

    async function load() {
      try {
        const res = await fetch('/api/standings/for-display', {
          headers: { authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          setErr(`HTTP ${res.status}`);
          return;
        }
        setErr(null);
        setRows((await res.json()) as StandingsRow[]);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'fetch failed');
      }
    }
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <ChannelChrome display={display} connected={connected} onUnpair={onUnpair}>
      <div className="mx-auto w-full max-w-5xl px-12 py-12">
        <div className="text-xs uppercase tracking-[0.4em] text-white/40">
          {display.venueName}
        </div>
        <h1 className="mt-3 text-6xl font-bold tracking-tight">
          League Standings
        </h1>

        {err && (
          <p className="mt-6 text-sm text-amber-300/80">
            Couldn't load standings: {err}
          </p>
        )}

        {!rows && !err && (
          <p className="mt-6 text-sm text-white/40">Loading…</p>
        )}

        {rows && rows.length === 0 && (
          <p className="mt-6 text-sm text-white/40">
            No completed games yet — standings will appear here as games
            finish.
          </p>
        )}

        {rows && rows.length > 0 && (
          <div className="mt-12 overflow-hidden rounded-2xl border border-white/10">
            <table className="w-full text-lg">
              <thead className="bg-white/5 text-sm uppercase tracking-wider text-white/50">
                <tr>
                  <th className="px-6 py-4 text-left">Team</th>
                  <th className="px-6 py-4 text-right">W</th>
                  <th className="px-6 py-4 text-right">L</th>
                  {rows.some((r) => r.ties > 0) && (
                    <th className="px-6 py-4 text-right">T</th>
                  )}
                  <th className="px-6 py-4 text-right">PCT</th>
                  <th className="px-6 py-4 text-right">PF</th>
                  <th className="px-6 py-4 text-right">PA</th>
                  <th className="px-6 py-4 text-right">DIFF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rows.map((r) => (
                  <tr key={r.teamId}>
                    <td className="px-6 py-4">
                      <span className="flex items-center gap-3">
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{
                            background: r.colorPrimary ?? '#3b82f6',
                          }}
                        />
                        {r.teamName}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-mono">{r.wins}</td>
                    <td className="px-6 py-4 text-right font-mono">
                      {r.losses}
                    </td>
                    {rows.some((rr) => rr.ties > 0) && (
                      <td className="px-6 py-4 text-right font-mono">
                        {r.ties}
                      </td>
                    )}
                    <td className="px-6 py-4 text-right font-mono">
                      {r.winPct.toFixed(3).slice(1)}
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-white/60">
                      {r.pointsFor}
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-white/60">
                      {r.pointsAgainst}
                    </td>
                    <td
                      className={`px-6 py-4 text-right font-mono ${
                        r.pointDifferential > 0
                          ? 'text-emerald-300'
                          : r.pointDifferential < 0
                          ? 'text-red-300'
                          : 'text-white/60'
                      }`}
                    >
                      {r.pointDifferential > 0
                        ? `+${r.pointDifferential}`
                        : r.pointDifferential}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ChannelChrome>
  );
}
